import type { FC } from "react";
import { Link } from "react-router";
import { getActiveHolidayNotice, type HolidayNotice } from "../../../constants/holidayNotice";
import type { OrderSummaryDTO } from "../../../libs/mypage/orderResolver";
import { Container } from "../components/Container";
import { HolidayNoticeBanner } from "../components/HolidayNoticeBanner";
import { Notice } from "../components/Notice";
import { OrderSummaryRow } from "../components/OrderSummaryRow";
import { PageHeading } from "../components/PageHeading";
import { TryAnotherEmailNotice } from "../components/TryAnotherEmailNotice";
import { useOrders } from "../hooks/useOrders";

/**
 * お客様にお願いしていることがある注文 (ご対応のお願い)。各行のステータスの下に何をお願いしているかを
 * 出す。支払い前の注文は注文から 7 日で自動キャンセルされる (Shopify ワークフロー) ので、そのときだけ
 * 期限の注意を添える。
 */
const ordersNeedingAction = (orders: OrderSummaryDTO[] | null): OrderSummaryDTO[] =>
  orders?.filter((o) => o.actionItems.length > 0) ?? [];

const hasPendingPayment = (orders: OrderSummaryDTO[]): boolean =>
  orders.some((o) => o.actionItems.some((a) => a.kind === "payment"));

type Props = {
  /**
   * 表示する長期休暇の告知。既定では現在日 (JST) から判定する。Storybook / テストで
   * 「告知あり / なし」を現在日に左右されず固定できるよう props で差し込めるようにしている。
   */
  holidayNotice?: HolidayNotice | null;
};

export const DashboardPage: FC<Props> = ({ holidayNotice = getActiveHolidayNotice("orders") }) => {
  const { orders, loading, error, refetch } = useOrders();
  const needingAction = ordersNeedingAction(orders);

  return (
    <Container>
      <PageHeading
        title="注文履歴"
        description="SurvaQストア・CALQS公式サイトでのご注文を新しい順に表示しています。"
      />
      <HolidayNoticeBanner notice={holidayNotice} />
      <ActionNotice orders={orders ?? []} needingAction={needingAction} />
      {loading && !orders ? (
        <p className="py-8 text-center text-neutral-500 text-sm">読み込み中…</p>
      ) : error && !orders ? (
        <div className="rounded border border-neutral-200 bg-neutral-50 px-5 py-12 text-center text-neutral-700 text-sm">
          <p className="mb-3">注文情報の取得に失敗しました。</p>
          <button
            type="button"
            onClick={() => {
              void refetch();
            }}
            className="text-neutral-700 underline underline-offset-4 hover:text-neutral-900"
          >
            再試行する
          </button>
        </div>
      ) : !orders || orders.length === 0 ? (
        <TryAnotherEmailNotice variant="empty" />
      ) : (
        <>
          <ul className="divide-y divide-neutral-200 border-neutral-200 border-t border-b">
            {orders.map((order) => (
              <li key={`${order.store}-${order.code}`}>
                <OrderSummaryRow order={order} />
              </li>
            ))}
          </ul>
          <TryAnotherEmailNotice variant="subtle" />
        </>
      )}
      <div className="mt-12 space-y-2 border-neutral-200 border-t pt-6 text-neutral-500 text-sm">
        <p>
          ご注文に関するお問い合わせは{" "}
          <Link to="/contact" className="underline underline-offset-4 hover:text-neutral-900">
            お問い合わせ先
          </Link>{" "}
          をご確認ください。
        </p>
        <p className="text-xs">
          Amazon・楽天市場・Yahoo! ショッピングでご購入の方は{" "}
          <Link
            to="/external-mall-contact"
            className="underline underline-offset-4 hover:text-neutral-900"
          >
            こちら
          </Link>
        </p>
      </div>
    </Container>
  );
};

/**
 * 一覧の上のお知らせ。ほとんどのお客様は注文が 1 件なので、そのときは行の「ご対応のお願い」で足り、
 * 件数や「ご注文があります」を重ねると冗長になる。1 件のときは期限のある支払いだけを知らせ、
 * 複数のときだけどの注文に対応が要るかを件数で知らせる。
 */
const ActionNotice: FC<{ orders: OrderSummaryDTO[]; needingAction: OrderSummaryDTO[] }> = ({
  orders,
  needingAction,
}) => {
  if (needingAction.length === 0) return null;
  const paymentDeadline = hasPendingPayment(needingAction) && (
    <p>
      {orders.length > 1 ? "お支払いが完了していないご注文は、" : ""}ご注文日から 7
      日を過ぎますと自動的にキャンセルとなります。お早めにお手続きください。
    </p>
  );
  const reflectNote = (
    <p className="mt-2 text-[11px] text-amber-900/80 leading-relaxed">
      ※ お手続き直後で反映前の場合は少々お待ちください。
    </p>
  );

  if (orders.length === 1) {
    if (!paymentDeadline) return null;
    return (
      <Notice variant="warn" title="お支払いが完了していません" className="mb-6">
        {paymentDeadline}
        {reflectNote}
      </Notice>
    );
  }

  return (
    <Notice
      variant="warn"
      title={`ご対応が必要なご注文があります (${needingAction.length} 件)`}
      className="mb-6"
    >
      <p>下記の「ご対応のお願い」が付いたご注文を開いて、ご確認ください。</p>
      {paymentDeadline && <div className="mt-2">{paymentDeadline}</div>}
      {reflectNote}
    </Notice>
  );
};
